import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Checkbox from '@rsos/components/capstone/base/Checkbox';
import Text from '@rsos/components/capstone/base/Text';
import { setSignature } from '../../actions';
import { Button, Input } from '../capstoneLegacy';

const LicenseAgreement = ({ handleStatus }) => {
  const dispatch = useDispatch();

  let navigate = useNavigate();

  const currentPSAP = useSelector(state => state.auth.currentPSAP);

  const firstName = useSelector(state => state.auth.userInfo.first_name);

  const lastName = useSelector(state => state.auth.userInfo.last_name);

  const [state, setState] = useState({
    signature: '',
    signatureError: '',
    isCheckBoxChecked: false,
    isSignatureValid: false,
  });

  const {
    signature,
    signatureError,
    isCheckBoxChecked,
    isSignatureValid,
  } = state;

  const handleError = value => {
    let error;

    const rexp = /^[\w -.]{3,50}$/;

    if (!rexp.test(value.trim())) {
      error = (
        <ul>
          <li>
            Must be between 3 and 50 characters (does not include leading or
            trailing spaces)
          </li>
          <li>
            Only alphanumeric characters, dashes, underscores, and periods
            allowed
          </li>
        </ul>
      );
    }

    return error;
  };

  const isBtnDisabled = () => {
    return isCheckBoxChecked && isSignatureValid;
  };

  const btnDisabled = !isBtnDisabled();

  const onChange = e => {
    // const rexp = /^[\w -.]{3,50}$/;
    const error = handleError(e.target.value);

    setState(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value,
      [`${e.target.name}Error`]: handleError(e.target.value),
      isSignatureValid: !error,
    }));
  };

  const onCheckBoxChange = e => {
    const target = e.target;
    const value = target.checked;

    setState(prevState => ({
      ...prevState,
      isCheckBoxChecked: value,
    }));
  };

  const submitAgreement = e => {
    e.preventDefault();

    if (!btnDisabled) {
      const psapInfo = currentPSAP;

      if (typeof psapInfo.contact_phone === 'undefined') {
        psapInfo['contact_phone'] = '10000000000';
      }
      if (psapInfo['contact_name'] === '') {
        psapInfo['contact_name'] = `${firstName} ${lastName}`;
      }

      dispatch(setSignature(signature));

      localStorage.setItem('signature', signature);

      navigate('/sign-up/integration-info');

      handleStatus(true);
    }
  };

  const termsOfUse = () => {
    return (
      <Text size="normal">
        By checking this box and clicking ‘submit,’ I: (1) acknowledge that I
        have read the license agreement and RapidSOS Terms of Use; (2) represent
        that I am authorized to enter into these agreements on behalf of the Agency;
        and (3) agree to be bound by the license agreement
      </Text>
    );
  };

  return (
    <div className="license-agreement-container hero">
      <form className="pt-20" onSubmit={submitAgreement}>
        <div className="sign-up-wrapper bubble-blue-bg">
          <div className="custom-page-title black-text">Emergency Communication Center End User License Agreement</div>
          <div className="agreement-container">
            Effective Date: January 31, 2024
            <div className="has-text-weight-bold pt-20">
              <strong>Definitions</strong>
            </div>
            <ul>
              <li>
                <strong>RapidSOS</strong>: means RapidSOS, Inc., a provider of
                emergency communications services, located at 3 Park Avenue,
                22nd Floor, New York, NY 10016, and its affiliates and
                subsidiaries.
              </li>
              <li>
                <strong>Agency</strong>: means a Public Safety Answering Point
                (PSAP) or Emergency Communications Center (ECC), as defined in
                the rules of the Federal Communications Commission (FCC),
                requesting access to RapidSOS Emergency Information.
              </li>
              <li>
                <strong>RapidSOS Emergency Information</strong>: means information
                received by RapidSOS from RapidSOS Emergency Information Providers
                and provided by RapidSOS to Agency, or exchanged via an authorized
                third-party integration, or provided directly by the Agency or an
                authorized third-party, including but not limited to supplemental
                9-1-1 caller location data, profile data associated with a 9-1-1
                caller, or other 9-1-1 incident data.
              </li>
              <li>
                <strong>Digital Alerts</strong>: means alerts from monitoring companies
                and third parties for specific critical events such as active assailants,
                train derailments, and alarms.
              </li>
              <li>
                <strong>RapidSOS Emergency Information Provider</strong>: means any person
                that provides RapidSOS Emergency Information to RapidSOS via wire or radio
                communications, including without limitation manufacturers of smartphones
                or other connected devices, operating systems service providers, application
                service providers, monitoring companies, etc.
              </li>
            </ul>
            <br />
            <br />
            <div className="has-text-weight-bold">
              <strong>RapidSOS Emergency Information Terms of Use</strong>
            </div>
            Agency explicitly acknowledges that the RapidSOS Emergency Information
            is provided for informational purposes only and is supplemental to location
            data provided from other sources, and that RapidSOS Emergency Information
            should not replace other emergency location information and should not be
            exclusively relied upon in an emergency scenario. RapidSOS Emergency Information
            will only be used by Agency to respond to 9-1-1 emergency events. Agency will
            not use the RapidSOS Emergency Information in any manner or for any purpose that
            violates any applicable local, state, national, or international law, any right
            of any person, or use the RapidSOS Emergency Information for any illegal, unauthorized
            or other improper purposes. Agency specifically authorizes RapidSOS, and each RapidSOS
            Emergency Information Provider, to provide the other emergency communications services
            contemplated herein to provision RapidSOS Emergency Information to Agency.
            <br />
            Agency further acknowledges that it has Digital Alerts enabled and may receive relevant
            alert types. With Digital Alerts, the alert notifying the Agency of an applicable emergency
            event may be displayed concurrent with a phone call from a Safety Agent or other method.
            Agency specifically acknowledges that the alert remains for informational purposes only
            and is supplemental to location data and emergency information from other sources.
            <br />
            <ul>
              <li>
                Your Agency is specifically requesting data to be presented through the Digital
                Alerts service and receipt of such data may require changes to your operational
                workflow when responding to an emergency. RapidSOS shall be held harmless for any
                liability arising from such use or failure to use.
              </li>
              <li>
                Your Agency acknowledges that RapidSOS is only transmitting the data rather than
                originating the data; therefore RapidSOS cannot guarantee the accuracy of data
                provided or originated by a third party and shall be held harmless for any liability
                arising from any use or reliance on such data.
              </li>
              <li>
                Your Agency acknowledges that RapidSOS will make reasonable efforts to digitally
                display requests for dispatch of emergency services and that a phone call request
                may not be placed.
              </li>
              <li>
                Your Agency has been trained on how to use and respond to the Digital Alerts product.
                This is not to be confused with Supplemental Alerts, where a phone call remains the
                primary request for service.
              </li>
              <li>
                Your Agency acknowledges that RapidSOS does not warrant or represent that all digitally
                displayed requests through Digital Alerts shall be free of error. Your agency further
                acknowledges that an act, omission, or failure may occur that prevents notification
                of a request from being received by your agency. Where an error, act, omission, or
                failure occurs that prevents notification of a request from being received, RapidSOS
                shall not be held liable.
              </li>
              <li>
                Your Agency acknowledges that false alarms may occur and that RapidSOS makes no warranties
                or representations, express, implied, or otherwise, as to the validity, accuracy, completeness,
                or performance of a request for dispatch displayed through the Digital Alerts service.
                ALL DATA RELATING TO THE DIGITAL ALERTS SERVICE IS PROVIDED “AS IS.”
              </li>
              <li>
                You agree that RapidSOS may note that your agency is capable of receiving Digital Alerts and
                that RapidSOS may use the name of your agency on company materials indicating agencies that may
                receive Digital Alerts.
              </li>
            </ul>
            <br />
            Agency acknowledges and agrees that RapidSOS may from time to time
            collect and analyze data arising from the use of the RapidSOS
            Emergency Information in order to facilitate the delivery of the
            RapidSOS Emergency Information, improve RapidSOS’s delivery of the
            RapidSOS Emergency Information and Agency’s experience, provide
            anonymized data to current and potential Emergency Information
            Providers, and improve or develop RapidSOS services. RapidSOS shall
            not share this data with any third person except as explained in
            this End User License Agreement, as authorized by Agency, and for
            the purposes of delivering the RapidSOS Emergency Information or to
            the extent required by a subpoena, court order, agency action, or
            any other legal or regulatory requirement.
            <br />
            <br />
            <div className="has-text-weight-bold">
              <strong>
                Credentials to Access RapidSOS Emergency Information
              </strong>
            </div>
            Agency acknowledges and agrees that RapidSOS may from time to time monitor, collect,
            and analyze data arising from the use of the RapidSOS Emergency Information, including,
            but not limited to, the receipt of requests for dispatch of emergency services, and/or
            records of actions taken by Agency and configuration, performance, usage, and consumption
            data relating to the use of RapidSOS services, in order (a) to facilitate the delivery
            of the RapidSOS Emergency Information or RapidSOS services, (b) improve RapidSOS’s delivery
            of the RapidSOS Emergency Information, RapidSOS services, and Agency’s experience, (c) provide
            anonymized data and analytics to current and potential RapidSOS Emergency Information
            Providers,  (d) improve or develop RapidSOS services, and (e) to ensure compliance with the
            terms of this Agreement. Agency further agrees to share certain data with RapidSOS and
            third-parties, such as alarm monitoring companies, in order to improve the product offering.
            RapidSOS shall not share this data with any third person except as explained in this End User
            License Agreement, as authorized by Agency, and for the purposes of delivering the RapidSOS
            Emergency Information or to the extent required by a subpoena, court order, agency action,
            or any other legal or regulatory requirement.
            <br />
            <br />
            <div className="has-text-weight-bold">
              <strong>Credentials to Access RapidSOS Emergency Information</strong>
            </div>
            Agency hereby requests a unique set of credentials assigned to Agency that will allow
            Agency to access RapidSOS Emergency Information. Agency acknowledges that Agency
            is responsible for proper safekeeping of those credentials, and will immediately notify
            RapidSOS electronically to support@rapidsos.com should these unique credentials ever get
            exposed to unauthorized personnel or otherwise compromised. For security reasons, RapidSOS
            reserves the right to disable credentials and re-issue new credentials at their sole
            discretion. In such case, Agency or Agency vendor will be notified by RapidSOS.
            <br />
            <br />
            <div className="has-text-weight-bold">
              <strong>Modifications</strong>
          </div>
          RapidSOS may modify the terms of this EULA at any time, with the modifications becoming
          effective upon the publication of this EULA. Agency will ensure that its Users agree at all
          times to the most recently published EULA provided by RapidSOS.
          </div>
          <div className="mt-20">
            <Input
              className="rl-input is-fullwidth"
              error={signatureError}
              labelName="Signature"
              labelPosition="top"
              maxLength={50}
              minLength={3}
              name="signature"
              onChange={onChange}
              placeholder="Signature"
              type="text"
              value={signature}
            />
            <span
              className={signatureError ? 'checkbox-signature-error' : null}
            >
              <Checkbox
                type="checkbox"
                label={termsOfUse()}
                name="license - checkbox"
                isChecked={isCheckBoxChecked}
                onChange={onCheckBoxChange}
                data-name="license-checkbox"
                noHoverBG
              />
            </span>
          </div>
        </div>
      </form>
      <div className="columns">
        <div className="column is-6">
          <div className="button-container">
            <Button
              buttonClicked={submitAgreement}
              className={
                'rapidlite-forms-button' +
                ' submit-agreement-button ml-10 ' +
                (btnDisabled ? 'unclickable' : 'clickable')
              }
              disabled={btnDisabled}
              id="submit-agreement-button"
              name="licenseAgreement"
            >
              SUBMIT
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseAgreement;
